import { useEffect, useState } from "react";
import { Plus, GraduationCap, Pencil, Trash2, FileDown, FileSpreadsheet, TrendingUp, TrendingDown } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { TRAINING_TAB } from "@/constants/testIds";
import { toast } from "sonner";

const EMPTY_FORM = { training_date: "", topic: "", cost: "", hours: "", provider: "" };

// Registro manual de capacitación (fecha, tema, costo, horas, proveedor opcional) — sin
// archivos ni evidencia adjunta, igual que los demás módulos del expediente. A diferencia de
// incidencias/acuses (evidencia legal inmutable), aquí SÍ se permite editar/borrar porque es
// un registro administrativo de costo/horas, no evidencia de cumplimiento.
export function TrainingTab({ employeeId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = () => {
    api.get(`/employees/${employeeId}/trainings`).then(({ data }) => setData(data));
  };

  useEffect(load, [employeeId]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ training_date: t.training_date, topic: t.topic, cost: String(t.cost), hours: String(t.hours), provider: t.provider || "" });
    setOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const payload = {
      training_date: form.training_date,
      topic: form.topic,
      cost: parseFloat(form.cost) || 0,
      hours: parseFloat(form.hours) || 0,
      provider: form.provider || null,
    };
    try {
      const { data } = editingId
        ? await api.put(`/employees/${employeeId}/trainings/${editingId}`, payload)
        : await api.post(`/employees/${employeeId}/trainings`, payload);
      toast.success(data.message);
      setOpen(false);
      setForm(EMPTY_FORM);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await api.delete(`/employees/${employeeId}/trainings/${id}`);
      toast.success(data.message);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleExport = async (format) => {
    const { data } = await api.get(`/employees/${employeeId}/trainings/export?format=${format}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `capacitacion.${format === "pdf" ? "pdf" : "csv"}`;
    a.click();
  };

  if (!data) return null;

  const pct = data.vs_company_avg_cost_pct;

  return (
    <div data-testid={TRAINING_TAB.section} className="mt-4 space-y-4">
      <div className="bg-card border border-petroleum/10 rounded-2xl p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex gap-6">
            <div>
              <p className="text-xs text-charcoal/50">Invertido en total</p>
              <p data-testid={TRAINING_TAB.totalCost} className="text-2xl font-heading font-bold text-charcoal">${(data.total_cost ?? 0).toLocaleString("es-MX")}</p>
            </div>
            <div>
              <p className="text-xs text-charcoal/50">Horas totales</p>
              <p data-testid={TRAINING_TAB.totalHours} className="text-2xl font-heading font-bold text-charcoal">{(data.total_hours ?? 0).toLocaleString("es-MX")} h</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button data-testid={TRAINING_TAB.exportCsvButton} size="sm" variant="outline" onClick={() => handleExport("csv")} className="rounded-full border-petroleum/30 text-petroleum gap-1.5">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </Button>
            <Button data-testid={TRAINING_TAB.exportPdfButton} size="sm" variant="outline" onClick={() => handleExport("pdf")} className="rounded-full border-petroleum/30 text-petroleum gap-1.5">
              <FileDown className="w-4 h-4" /> PDF
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid={TRAINING_TAB.addButton} size="sm" onClick={openCreate} className="bg-petroleum rounded-full gap-1.5">
                  <Plus className="w-4 h-4" /> Registrar capacitación
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-bone">
                <DialogHeader>
                  <DialogTitle>{editingId ? "Editar capacitación" : "Registrar capacitación"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Fecha</Label>
                    <Input data-testid={TRAINING_TAB.dateInput} type="date" value={form.training_date} onChange={(e) => setForm({ ...form, training_date: e.target.value })} className="bg-white" />
                  </div>
                  <div>
                    <Label>Tema o nombre de la capacitación</Label>
                    <Input data-testid={TRAINING_TAB.topicInput} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Ej. Curso de manejo de montacargas" className="bg-white" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Costo (MXN)</Label>
                      <Input data-testid={TRAINING_TAB.costInput} type="number" min="0" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-white" />
                    </div>
                    <div>
                      <Label>Horas invertidas</Label>
                      <Input data-testid={TRAINING_TAB.hoursInput} type="number" min="0" step="0.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="bg-white" />
                    </div>
                  </div>
                  <div>
                    <Label>Proveedor / instructor (opcional)</Label>
                    <Input data-testid={TRAINING_TAB.providerInput} value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="bg-white" />
                  </div>
                  <Button data-testid={TRAINING_TAB.submitButton} onClick={handleSubmit} disabled={submitting || !form.training_date || !form.topic} className="w-full bg-petroleum rounded-full">
                    Guardar
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {pct !== null && (
          <div data-testid={TRAINING_TAB.companyComparison} className="flex items-center gap-1.5 mt-3 text-xs text-charcoal/60">
            {pct >= 0 ? <TrendingUp className="w-3.5 h-3.5 text-emerald-600" /> : <TrendingDown className="w-3.5 h-3.5 text-terracotta" />}
            {pct >= 0
              ? `${pct}% por encima del promedio de la empresa`
              : `${Math.abs(pct)}% por debajo del promedio de la empresa`}
            {" "}(${(data.company_avg_cost ?? 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })} promedio por persona)
          </div>
        )}
      </div>

      <div className="space-y-2">
        {data.items.map((t) => (
          <div key={t.id} data-testid={TRAINING_TAB.row} className="bg-card border border-petroleum/10 rounded-xl p-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <GraduationCap className="w-4 h-4 text-terracotta mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-medium text-charcoal">{t.topic}</p>
                <p className="text-xs text-charcoal/50">
                  {t.training_date}{t.provider ? ` · ${t.provider}` : ""} · capturado por {t.captured_by}
                  {t.edited_by && ` · editado por ${t.edited_by}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <div className="text-right">
                <p className="font-medium text-charcoal">${(t.cost ?? 0).toLocaleString("es-MX")}</p>
                <p className="text-xs text-charcoal/50">{t.hours} h</p>
              </div>
              <button data-testid={`${TRAINING_TAB.editButton}-${t.id}`} onClick={() => openEdit(t)} className="text-charcoal/40 hover:text-petroleum">
                <Pencil className="w-4 h-4" />
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid={`${TRAINING_TAB.deleteButton}-${t.id}`} className="text-charcoal/40 hover:text-terracotta">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-bone">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar este registro de capacitación?</AlertDialogTitle>
                    <AlertDialogDescription>"{t.topic}" del {t.training_date} se eliminará permanentemente. Esta acción no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction data-testid={TRAINING_TAB.confirmDeleteButton} onClick={() => handleDelete(t.id)} className="bg-terracotta hover:bg-terracotta/90">Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
        {data.items.length === 0 && <p className="text-charcoal/50 text-sm">Sin capacitaciones registradas para esta persona.</p>}
      </div>
    </div>
  );
}
